import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { ExternalButton, LinkButton } from "../components/ui/Button";
import { Comparison } from "../sections/Comparison";
import { CTA } from "../sections/CTA";
import { colors, fonts, media, radius } from "../theme";

const requirements = [
  "A working mobile money account (MTN or Airtel)",
  "A national ID and a selfie",
  "A 90 day mobile money statement, we help you pull it",
  "Geo tagged photos of your stock or premises",
  "A trading licence, market chairman vouch, or SACCO vouch",
];

const useCases = [
  { t: "Stock up loans", d: "Buy stock for the next sales cycle without dipping into your working capital." },
  { t: "Shop expansion", d: "Open a second stall, hire help, or add new product lines." },
  { t: "Equipment purchase", d: "Scales, fridges, sewing machines, or salon equipment paid over flexible cycles." },
  { t: "Supply chain finance", d: "Bridge supplier deposits and invoices while waiting on customer or contract payments." },
];

export const Borrow = () => (
  <>
    <PageHeader
      eyebrow="For small businesses"
      title="Affordable working capital. No land titles required."
      sub="Everpesa loans are designed for the small businesses that banks rarely serve. Apply from your phone, get scored on your real business activity, and receive funds within hours."
    >
      <Row>
        <ExternalButton variant="white" href="https://invest.everpesa.com" target="_blank" rel="noopener noreferrer">
          Apply for a loan
        </ExternalButton>
        <LinkButton variant="outlineWhite" to="/contact">Talk to us</LinkButton>
      </Row>
    </PageHeader>

    <Section>
      <Container>
        <Split>
          <div>
            <Eyebrow>What you need</Eyebrow>
            <h2 style={{ marginTop: 18, marginBottom: 18, fontFamily: fonts.display }}>
              Five simple things. No collateral.
            </h2>
            <p style={{ fontSize: 17, marginBottom: 28 }}>
              We don't need land titles, payslips, or a bank statement. We
              score you on the way you actually run your business: your
              cashflow, your stock, and your standing in the community.
            </p>
            <List>
              {requirements.map((r) => (
                <Li key={r}><Tick />{r}</Li>
              ))}
            </List>
          </div>
          <PhotoStack>
            <PhotoA src={process.env.PUBLIC_URL + "/images/trader-mobilemoney.jpg"} alt="Trader checking mobile money on phone" />
            <PhotoB src={process.env.PUBLIC_URL + "/images/payment-receipt.jpg"} alt="Borrower confirming a payment" />
          </PhotoStack>
        </Split>
      </Container>
    </Section>

    <Section style={{ background: colors.surfaceAlt }}>
      <Container>
        <Eyebrow>What our loans fund</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 32, fontFamily: fonts.display }}>
          Real working capital for real businesses.
        </h2>
        <Grid>
          {useCases.map((u) => (
            <Card key={u.t}>
              <h3>{u.t}</h3>
              <p>{u.d}</p>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>

    <Comparison />

    <Section>
      <Container>
        <Eyebrow>The borrower journey</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 32, fontFamily: fonts.display, maxWidth: 16 + "ch" }}>
          From application to disbursement in hours.
        </h2>
        <Steps>
          <Step><b>1.</b><h3>Apply on your phone</h3><p>Submit your details and documents in a guided mobile flow. Need help? A field agent can complete the form on your behalf.</p></Step>
          <Step><b>2.</b><h3>Get scored and listed</h3><p>Our five layer model assigns a transparent risk tier and posts your loan to the marketplace for investors to fund.</p></Step>
          <Step><b>3.</b><h3>Receive your loan</h3><p>Once funded, the money is sent to your mobile money wallet. Repay over 30 to 90 days, then borrow again the next cycle.</p></Step>
        </Steps>
      </Container>
    </Section>

    <CTA />
  </>
);

const Row = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap; margin-top: 16px;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  ${media.md} { grid-template-columns: 1fr; gap: 40px; }
`;

const List = styled.ul`
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 14px;
`;

const Li = styled.li`
  display: flex; align-items: center; gap: 12px;
  color: ${colors.inkMuted};
  font-size: 16px;
`;

const Tick = styled.span`
  width: 24px; height: 24px; flex: 0 0 24px;
  border-radius: 50%;
  background: ${colors.brandSoft};
  color: ${colors.brand};
  position: relative;
  &::after {
    content: "✓";
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700;
  }
`;

const PhotoStack = styled.div`
  position: relative;
  height: 480px;
  ${media.md} { height: 380px; }
`;

const PhotoA = styled.img`
  position: absolute;
  top: 0; left: 0;
  width: 65%;
  height: 75%;
  object-fit: cover;
  border-radius: ${radius.xl};
  box-shadow: 0 24px 48px -12px rgba(10,15,31,0.2);
`;

const PhotoB = styled.img`
  position: absolute;
  bottom: 0; right: 0;
  width: 55%;
  height: 60%;
  object-fit: cover;
  border-radius: ${radius.xl};
  box-shadow: 0 24px 48px -12px rgba(10,15,31,0.2);
  border: 6px solid white;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  ${media.lg} { grid-template-columns: repeat(2, 1fr); }
  ${media.sm} { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  padding: 24px;
  h3 { font-family: ${fonts.display}; font-size: 18px; margin-bottom: 10px; }
  p { font-size: 14.5px; line-height: 1.6; }
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  ${media.md} { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: ${colors.surfaceAlt};
  border-radius: ${radius.lg};
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  b {
    font-family: ${fonts.display};
    font-size: 28px;
    color: ${colors.brand};
    font-weight: 800;
  }
  h3 { font-family: ${fonts.display}; font-size: 19px; }
  p { font-size: 15px; }
`;
